import ExifReader from 'exifreader';
import { useCallback, useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';

import { useSnackbar } from '@cms/providers/SnackbarProvider';
import { FIXED_SEARCH_PARAMS, POST_SET_VALUE_OPTIONS } from '@cms/utils/constants';
import { useAppSelector } from '@cms/utils/store/hooks';
import { groupBy, IDropdownOptionV2, isAssetUsed, ISystemAsset, SUBTYPES, TAssetType } from '@dl/declarations';
import api from '../../utils/api';
import { initialValues, schema, UpdateAssetFormData } from '../../utils/forms/asset';
import { generateSearchParam, renderDateTime } from '../../utils/helpers';
import { AssetsPath } from '../../utils/routes';
import { RenderModeType } from '../../utils/types';
import { CopyToClipboardButton } from '../data/Button';
import { FileInput } from '../data/FileInput';
import { ControlledRadio } from '../data/Radio';
import { ControlledStaticInput } from '../data/StaticInput';
import { ControlledSystemTextInput } from '../data/TextInput';
import { LabeledSection } from '../ui/LabeledSection';
import { FormContainer } from './FormContainer';

interface AssetFormProps {
    formContainerProps?: Partial<Omit<React.ComponentProps<typeof FormContainer<UpdateAssetFormData>>, "children" | "schema">>;
}

export const AssetForm: React.FC<AssetFormProps> = (props) => {
    const { formContainerProps } = props;

    const { showSnackbar } = useSnackbar();
    const [searchParams, setSearchParams] = useSearchParams()
    const mode = searchParams.get(generateSearchParam(AssetsPath, 'mode')) as RenderModeType;

    const { entity } = useAppSelector((state) => state.form)

    const handleSubmitRecord = async (data: UpdateAssetFormData) => {
        const formData = new FormData();

        const { file, ...restOfData } = data;

        if (file) formData.append('file', file)

        const payload: Record<string, {}> = {}

        Object.entries(restOfData).forEach(([key, value]) => {
            if (['subtypes', 'keywords'].includes(key)) {
                payload[key] = (value as string[]).join(',')
            }
            else {
                payload[key] = (value as string)
            }
        })

        formData.append('data', JSON.stringify(payload))

        let asset: ISystemAsset | null = null;

        if (mode === 'create') {
            const res = await api.post(`/assets`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
                timeout: 60000,
            });

            asset = res.data;
        }
        else if (mode === 'update') {
            const res = await api.patch(`/assets/${data._id}`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
                timeout: 60000,
            });

            asset = res.data;
        }

        if (asset?._id) {
            showSnackbar(`Asset ${mode === 'create' ? 'created' : 'updated'} successfully`, 'success');

            setSearchParams((prev) => {
                prev.set(generateSearchParam(AssetsPath, 'mode'), 'view')
                prev.set(generateSearchParam(AssetsPath, FIXED_SEARCH_PARAMS.SHOULD_REFRESH), "true")
                return prev;
            }, {
                preventScrollReset: true,
                replace: true,
            })
        }
    }

    const isUsed = isAssetUsed(entity as ISystemAsset)

    return (
        <FormContainer<UpdateAssetFormData>
            title='Asset'
            headerOptions={{
                show: false,
                createTitle: "Upload",
            }}
            formSize='full'
            queryOptions={{
                enabled: true,
                basePath: AssetsPath,
            }}
            deleteOptions={{
                show: false,
                disabled: isUsed,
                label: "Delete asset",
                tooltip: isUsed ? 'This asset is currently used and cannot be deleted' : undefined,
            }}
            schema={schema}
            defaultValues={initialValues}
            handleCreateRecordOverride={handleSubmitRecord}
            handleUpdateRecordOverride={handleSubmitRecord}
            shouldRedirectAfterSubmit
            {...formContainerProps}
        >
            <Form />
        </FormContainer>
    )
}

export const Form = () => {
    const { setValue, watch } = useFormContext<UpdateAssetFormData>();

    const [
        subtypes,
        headline,
        src_url,
        file,
        type,
        credit,
        copyright,
        created_at,
        updated_at
    ] = watch(['subtypes', 'headline', 'src_url', 'file', 'type', 'credit', 'copyright', 'created_at', 'updated_at']);

    const handleChangeFile = useCallback(async (files: FileList | null) => {
        const file = files && files.length > 0 ? files[0] : null;

        if (file) {
            const tags = await ExifReader.load(file);

            if (tags) {

                const headline = (tags['Headline'] || tags['description'] || tags['ImageDescription'])?.description || ''
                const copyright = (tags['Copyright'] || tags['Copyright Notice'])?.description || ''
                const credit = (tags['creator'] || tags['Source'])?.description || ''

                let keywords: Array<string> = []
                let keywordsTag = (tags['Keywords'])

                if (keywordsTag) {
                    if (Array.isArray(keywordsTag)) {
                        keywords = keywordsTag.map((item) => item.description);
                    }
                    else {
                        keywords = keywordsTag.description.split(',').map((item: string) => item.trim());
                    }
                }

                if (headline) setValue('headline', headline, POST_SET_VALUE_OPTIONS);
                if (credit) setValue('credit', credit, POST_SET_VALUE_OPTIONS);
                if (copyright) setValue('copyright', copyright, POST_SET_VALUE_OPTIONS);
                if (keywords.length > 0) setValue('keywords', keywords, POST_SET_VALUE_OPTIONS);
            }

            setValue("file", file, POST_SET_VALUE_OPTIONS);
        } else {
            setValue("file", null);
        }
    }, []);

    const subtypesLookupsToOptions = useMemo(() => {
        const mapped = ([
            SUBTYPES.NEWS,
            SUBTYPES.RESEARCH,
            SUBTYPES.SYSTEM,
        ]).map((item) => ({
            _id: item.ID,
            slug: item.SLUG,
            label: item.NAME,
            groupKey: 'Subtypes',
        }))

        return groupBy(mapped, (item) => item.groupKey);
    }, [])

    const imageTypes: Array<IDropdownOptionV2 & {
        slug: TAssetType
    }> = useMemo(() => {
        const subtype = subtypes[0]

        const types: Array<IDropdownOptionV2 & { slug: TAssetType }> = [
            { _id: '1', label: 'Illustration', slug: 'illustration', groupKey: 'Options' }
        ]

        if (subtype === SUBTYPES.NEWS.ID) {
            types.push({ _id: '2', label: 'Stock', slug: 'stock', groupKey: 'Options' })
        }

        if (subtype === SUBTYPES.RESEARCH.ID) {
            types.push(
                { _id: '2', label: 'Stock', slug: 'stock', groupKey: 'Options' },
                { _id: '3', label: 'Chainwire', slug: 'chainwire', groupKey: 'Options' }
            )
        }

        return types;
    }, [subtypes])

    useEffect(() => {
        const timerId = setTimeout(() => {
            let caption = ""

            if (headline) {
                caption += `${headline}; `
            }

            if (['stock', 'chainwire'].includes(type)) {
                if (copyright) {
                    caption += `Source: ${copyright}; `
                }
            }

            if (type === 'illustration') {
                if (credit) {
                    caption += `Illustration: ${credit}; `
                }

                if (copyright) {
                    caption += `Source: ${copyright};`
                }
            }

            setValue('article_caption', caption, POST_SET_VALUE_OPTIONS)
            setValue('image_caption', caption, POST_SET_VALUE_OPTIONS)
        }, 500);

        return () => {
            clearTimeout(timerId)
        }
    }, [headline, type, credit, copyright])

    useEffect(() => {
        const timerId = setTimeout(() => {
            setValue('meta_data.description', headline, POST_SET_VALUE_OPTIONS)
        }, 500);

        return () => {
            clearTimeout(timerId)
        }
    }, [headline])

    const identifier = src_url?.split('/')?.pop() || '';

    return (
        <div className='w-full flex flex-col gap-8'>
            <div className='px-20 mt-8 w-full flex flex-col gap-8 lg:grid lg:grid-cols-4 bg-cs-200'>
                <div className='col-span-1 py-4 bg-cs-200 flex flex-col gap-8'>
                    <LabeledSection
                        label='Category'
                    >
                        <ControlledRadio
                            name='subtypes'
                            options={subtypesLookupsToOptions}
                            valueKey='_id'
                            allowMultiSelect
                            maxSelectionForMultiSelect={1}
                            showRadio
                        />
                    </LabeledSection>

                    <LabeledSection
                        label='Type'
                    >
                        <ControlledRadio
                            name='type'
                            options={{
                                Options: imageTypes
                            }}
                            valueKey='slug'
                            showRadio
                        />
                    </LabeledSection>
                </div>
                <div className='col-span-2'>
                    <FileInput
                        {...(file ? { file } : { srcUrl: src_url })}
                        className='w-full h-96'
                        handleChange={handleChangeFile}
                        showCorners={false}
                        showFullPreview
                        mainAction={{
                            show: src_url ? false : true,
                            actionText: 'Upload file',
                        }}
                    />
                </div>
                <div className='col-span-1 py-4 bg-cs-200 flex flex-col gap-8'>
                    <LabeledSection
                        disabled={!identifier}
                        label='Identifier'
                        rightIcon={(
                            <CopyToClipboardButton
                                text={identifier}
                            />
                        )}
                    >
                        {identifier}
                    </LabeledSection>

                    <LabeledSection
                        disabled={!src_url}
                        label='URL'
                        rightIcon={(
                            <CopyToClipboardButton
                                text={src_url}
                            />
                        )}
                    >
                        {src_url}
                    </LabeledSection>

                    {
                        created_at && (
                            <LabeledSection
                                label='Created date'
                            >
                                <time dateTime={created_at}>
                                    {renderDateTime(created_at)}
                                </time>
                            </LabeledSection>
                        )
                    }

                    {
                        updated_at && (
                            <LabeledSection
                                label='Last updated date'
                            >
                                <time dateTime={updated_at}>
                                    {renderDateTime(updated_at)}
                                </time>
                            </LabeledSection>
                        )
                    }
                </div>
            </div>
            <div className='px-20 w-full flex flex-col gap-2 lg:grid lg:grid-cols-4 '>
                <div className='col-span-1' />
                <div className='flex flex-col gap-4 col-span-2'>
                    <ControlledSystemTextInput
                        name='headline'
                        description='Headline'
                        inputSize='full'
                    />

                    {/* <ControlledSystemTextInput
                        name='image_caption'
                        description='Caption'
                        inputSize='full'
                        placeholder='Add image caption'
                    /> */}

                    {/* <ControlledSystemTextInput
                        name='meta_data.description'
                        description='SEO Description'
                        inputSize='full'
                    /> */}

                    <ControlledSystemTextInput
                        name='credit'
                        description='Credit'
                        inputSize='full'
                        placeholder='Add Illustration credits'
                    // hidden={type !== 'illustration'}
                    />

                    <ControlledSystemTextInput
                        name='copyright'
                        description='Copyright'
                        inputSize='full'
                        placeholder='Add copyright from the stock image'
                    // hidden={type !== 'stock'}
                    />

                    <ControlledStaticInput
                        name='keywords'
                        description='Keywords'
                        inputSize='full'
                    />

                    <ControlledSystemTextInput
                        name='article_caption'
                        description='Article Caption'
                        inputSize='full'
                    />
                </div>
                <div className='col-span-1' />
            </div>
        </div>
    )
}